import { TimeFrameKey, timeFrames } from 'data/time-frames';
import { useProductsWithHoldingsAndRates } from 'hooks/products/useProducts';
import api from 'services/api';
import {
  GraphPoint,
  PortfolioGainLossDto,
  PortfolioV2Api,
} from 'services/generated';
import { handleErrors } from 'services/helpers';
import { rootApi } from 'store/root-api';

const portfolioClient = new PortfolioV2Api(undefined, undefined, api);

const gainsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getPortfolioGainsAndLosses: builder.query<
      PortfolioGainLossDto,
      TimeFrameKey
    >({
      queryFn: timeInterval =>
        handleErrors(() =>
          portfolioClient.getPortfolioGainsAndLosses(
            timeFrames[timeInterval].valueDays,
          ),
        ),
      providesTags: ['PortfolioGainLoss'],
    }),
    getPortfolioPerformanceGraph: builder.query<GraphPoint[], TimeFrameKey>({
      queryFn: timeInterval =>
        handleErrors(() =>
          portfolioClient.getPortfolioPerformanceGraph(
            timeFrames[timeInterval].valueDays,
          ),
        ),
    }),
  }),
});

export function usePortfolioGainsAndLosses(timeFrame: TimeFrameKey) {
  const { data: gainsAndLosses, isLoading: isGainsAndLossesLoading } =
    gainsApi.useGetPortfolioGainsAndLossesQuery(timeFrame);
  const { totalPortfolioValue } = useProductsWithHoldingsAndRates();

  if (gainsAndLosses) {
    // If we have a more up-to-date portfolio value, we use that as the opening balance and recalculate the G/L
    if (totalPortfolioValue != null) {
      var netCashFlow =
        gainsAndLosses.positiveCashFlow - gainsAndLosses.negativeCashFlow;
      var gainAmount =
        totalPortfolioValue - gainsAndLosses.openingBalance - netCashFlow;

      var numerator = totalPortfolioValue + gainsAndLosses.negativeCashFlow;
      var denominator =
        gainsAndLosses.openingBalance + gainsAndLosses.positiveCashFlow;

      var gainPercentage =
        denominator == 0 ? 0 : (numerator / denominator - 1) * 100;

      return {
        gainsAndLosses: {
          openingTime: gainsAndLosses.openingTime,
          gainAmount,
          gainPercentage,
        },
        isLoading: isGainsAndLossesLoading,
      };
    } else {
      // Fallback to returing the G/L returned by the endpoint if no newer portfolio value is available
      return {
        gainsAndLosses: {
          openingTime: gainsAndLosses.openingTime,
          gainAmount: gainsAndLosses.gainAmount,
          gainPercentage: gainsAndLosses.gainPercentage,
        },
        isLoading: isGainsAndLossesLoading,
      };
    }
  }

  return { isLoading: isGainsAndLossesLoading };
}

export const { useGetPortfolioPerformanceGraphQuery } = gainsApi;
